import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  FormFeedback,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { State } from "../../interfaces";
import { ActivityButton, SequenceNumberInput } from "../../components/Common";
import { ImageData } from "../../interfaces";
import { MediaDto } from "../../interfaces/media";
import {
  CreateFlashCardRequest,
  FlashCardDto,
} from "../../interfaces/flash-card";
import {
  addFlashCard,
  getFlashCard,
  updateFlashCard,
  uploadFlashCardImage,
} from "../../store/actions";
import { SequenceEnum } from "../../enums";
import ImageUploader from "../../components/Common/ImageUploader";

type TProps = {
  id?: string | null;
  show: boolean;
  onCloseClick: (event: any) => void;
  lessonId: string;
};
export const AddEditFlashCardModal: React.FC<TProps> = ({
  id,
  show,
  onCloseClick,
  lessonId,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [request] = useState<CreateFlashCardRequest>();
  const { loading, success, error } = useSelector(
    (state: any): State<FlashCardDto> => state.flashCard.addFlashCard
  );

  const {
    loading: updateFlashCardLoading,
    success: updateFlashCardSuccess,
    error: updateFlashCardError,
  } = useSelector(
    (state: any): State<FlashCardDto> => state.flashCard.updateFlashCard
  );

  const {
    loading: uploadingFlashCardImage,
    success: uploadFlashCardImageSuccess,
    error: uploadFlashCardImageError,
    response: uploadFlashCardImageResponse,
  } = useSelector(
    (state: any): State<MediaDto> => state.flashCard.uploadFlashCardImage
  );

  const { error: getFlashCardError, response: flashCard } = useSelector(
    (state: any): State<FlashCardDto> => state.flashCard.flashCard
  );

  //#region form
  const form = useFormik<CreateFlashCardRequest>({
    enableReinitialize: true,
    initialValues: {
      imageId: request?.imageId,
      lessonId: request?.lessonId || lessonId,
      sequenceNumber: request?.sequenceNumber || 0,
    },
    initialErrors: error || updateFlashCardError,
    validationSchema: Yup.object({
      lessonId: Yup.string(),
      imageId: Yup.string().required(
        t("isNotEmpty", {
          displayName: "arabicValue",
        })
      ),
      sequenceNumber: Yup.number().required(
        t("isNotEmpty", {
          displayName: "sequenceNumber",
        })
      ),
    }),
    onSubmit: (values) => {
      if (id) {
        dispatch(updateFlashCard(id, values, history));
      } else {
        dispatch(addFlashCard(values, history));
      }
    },
  });
  //#endregion

  //#region effects
  useEffect(() => {
    if (
      (!loading && success) ||
      (!updateFlashCardLoading && updateFlashCardSuccess)
    ) {
      onClose();
    }
  }, [loading, updateFlashCardLoading]);

  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onChangeFlashCardImage = (images: ImageData[]) => {
    const files = images.map((m) => m.file);
    if (files && files.length > 0) {
      const imageFile = files[0];
      if (
        imageFile &&
        !imageFile.name.match(
          /\.(jpg|JPG|jpeg|JPEG|jfif|pjpeg|pjp|png|PNG|webp|gif|GIF)$/
        )
      ) {
        toast.error(t("selectValidImage"), {
          autoClose: 3000,
        });
        return false;
      }
      if (imageFile) {
        dispatch(uploadFlashCardImage(imageFile, history, flashCard?.id));
      }
    }
  };

  //#endregion

  //#region effects

  useEffect(() => {
    if (id) {
      dispatch(getFlashCard(id, history));
    }
  }, [id]);

  useEffect(() => {
    if (id && flashCard) {
      form.setValues({
        imageId: flashCard.image?.id,
        lessonId: flashCard.lesson.id,
        sequenceNumber:
          form.values.sequenceNumber || flashCard.sequenceNumber || 0,
      });
    }
  }, [flashCard]);

  useEffect(() => {
    if (
      !uploadingFlashCardImage &&
      uploadFlashCardImageSuccess &&
      uploadFlashCardImageResponse
    ) {
      form.setFieldValue("imageId", uploadFlashCardImageResponse.id);
    }
  }, [uploadingFlashCardImage]);

  //#endregion

  return (
    <React.Fragment>
      <ToastContainer />
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
        className="modal-xl"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {id
              ? t("editItem", { item: "flashCard" })
              : t("addItem", { item: "flashCard" })}
          </ModalHeader>
          {typeof uploadFlashCardImageError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{uploadFlashCardImageError}</p>
            </Alert>
          )}
          {typeof error === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{error}</p>
            </Alert>
          )}
          {typeof getFlashCardError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{getFlashCardError}</p>
            </Alert>
          )}
          {typeof updateFlashCardError === "string" && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">{updateFlashCardError}</p>
            </Alert>
          )}
          <ModalBody>
            <Row>
              <Col sm={4}>
                <SequenceNumberInput
                  tabIndex={1}
                  value={form.values.sequenceNumber}
                  onChange={(e) => {
                    e.preventDefault();
                    const { value } = e.target;
                    const regex =
                      /^(0*[0-9][0-9]*(\.[0-9]*)?|0*\.[0-9]*[1-9][0-9]*)$/;
                    if (regex.test(value.toString())) {
                      form.setFieldValue("sequenceNumber", value);
                    } else {
                      form.setFieldValue("sequenceNumber", "");
                    }
                  }}
                  onBlur={form.handleBlur}
                  invalid={
                    form.touched.sequenceNumber && form.errors.sequenceNumber
                      ? true
                      : false
                  }
                  sequenceType={SequenceEnum.FLASH_CARD}
                  onGenerateSequenceNumber={(v: number) => {
                    form.setFieldValue("sequenceNumber", v);
                  }}
                  mode={id ? "edit" : "add"}
                  request={{
                    type: SequenceEnum.FLASH_CARD,
                    lessonId: lessonId,
                  }}
                />
                {form.touched.sequenceNumber && form.errors.sequenceNumber ? (
                  <FormFeedback type="invalid">
                    {form.errors.sequenceNumber as string}
                  </FormFeedback>
                ) : null}
              </Col>
              <div className="12">
                <ImageUploader
                  multiple={false}
                  initialImages={
                    id && flashCard?.image?.path ? [flashCard?.image?.path] : []
                  }
                  removable={false}
                  onChangeImages={(images: ImageData[]) =>
                    onChangeFlashCardImage(images)
                  }
                />
              </div>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={loading || updateFlashCardLoading}
              disabled={
                loading ||
                updateFlashCardLoading ||
                uploadingFlashCardImage ||
                getFlashCardError
              }
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
